import cloneDeep from 'lodash/cloneDeep';
import { v4 } from 'uuid';
import type { BaseCondition, OfferDefinition, OfferSystemConfig } from './offerSystemTypes';

interface PurchaseData {
  offerId: string;
  activationId: string;
  date: number;
  usdCentPrice: number;
}

interface ActivationData {
  current: {
    activationId: string;
    variant: number;
    startDate: number;
  } | null;
  lastDeactivation: number | null;
  activationNumber: number;
}

interface OfferDataEntry {
  activation: ActivationData;
  purchased: PurchaseData[];
}

interface SystemHistoryActiveOfferData<C extends BaseCondition, GD> {
  offer: OfferDefinition<C, GD>;
  activationId: string;
  variant: number;
  startDate: number;
}

export type OfferSystemData = Record<string, OfferDataEntry>;

export type PurchasesList = PurchaseData[];

export class OfferSystemHistory<S, C extends BaseCondition, GD> {
  private readonly history: OfferSystemData;

  private readonly saveHandler: (history: OfferSystemData) => void;

  constructor(private readonly config: OfferSystemConfig<S, C, GD>) {
    this.history = cloneDeep(config.purchaseHistory);
    this.saveHandler = config.saveHandler;
  }

  public activateOffer(offerId: string, variant: number): void {
    const activationData = this.getOfferHistory(offerId).activation;

    activationData.current = {
      activationId: this.getActivationId(),
      variant,
      startDate: this.config.getCurrentTime(),
    };
    activationData.activationNumber += 1;

    this.saveHandler(this.history);
  }

  private getActivationId(): string {
    return v4();
  }

  private getOfferHistory(offerId: string): OfferDataEntry {
    if (!this.history[offerId]) {
      this.history[offerId] = {
        purchased: [],
        activation: { lastDeactivation: null, current: null, activationNumber: 0 },
      };
    }
    return this.history[offerId];
  }

  public deactivateOffer(offerId: string, date: number = this.config.getCurrentTime()): void {
    const data = this.history[offerId];
    data.activation.lastDeactivation = date;
    data.activation.current = null;
    this.saveHandler(this.history);
  }

  public onPurchased(offerId: string): void {
    const currentActivation = this.getCurrentActivationData(offerId).current;
    const offer = this.config.offers.find(({ id }) => id === offerId) ?? null;
    if (!offer) return;

    if (!currentActivation) {
      console.warn(`Cannot find activation data for ${offerId}`);
    }

    let usdCentPrice = 0;

    if (currentActivation && offer.variants[currentActivation.variant]) {
      usdCentPrice = offer.variants[currentActivation.variant].iapCostCents;
    }

    this.getOfferHistory(offerId).purchased.push({
      offerId,
      activationId: currentActivation?.activationId ?? `Err_${this.getActivationId()}`,
      date: this.config.getCurrentTime(),
      usdCentPrice,
    });
    this.saveHandler(this.history);
  }

  public getCurrentActivationId(offerId: string): string | null {
    return this.getOfferHistory(offerId).activation.current?.activationId ?? null;
  }

  public getCurrentActivationData(offerId: string): ActivationData {
    return this.getOfferHistory(offerId).activation;
  }

  public getShownAmount(offerId: string): number {
    return this.getOfferHistory(offerId).activation.activationNumber;
  }

  public getBoughtAmount(offerId: string): number {
    return this.getOfferHistory(offerId).purchased.length;
  }

  public getActivationTime(offerId: string): number | null {
    return this.getOfferHistory(offerId).activation?.current?.startDate ?? null;
  }

  public getLastDeactivationTime(offerId: string): number | null {
    return this.getOfferHistory(offerId).activation.lastDeactivation ?? null;
  }

  public getLastTimeBought(offerId: string): number | null {
    const purchaseData = this.getOfferHistory(offerId).purchased;
    if (purchaseData.length === 0) return null;
    return Object.values(purchaseData).reduce((a, b) => Math.max(a, b.date), 0);
  }

  public getInstanceBoughtAmount(offerId: string, activationId: string): number {
    return this.getOfferHistory(offerId).purchased.filter((entry) => entry.activationId === activationId).length;
  }

  public getAllPurchases(): PurchasesList {
    const result: PurchasesList = [];

    Object.keys(this.history).forEach((offerId) => {
      const { purchased } = this.getOfferHistory(offerId);
      purchased.forEach(({ activationId, date, usdCentPrice }) => {
        result.push({
          offerId,
          activationId,
          date,
          usdCentPrice,
        });
      });
    });

    return result;
  }

  public getActiveOffers(): SystemHistoryActiveOfferData<C, GD>[] {
    const result: { offer: OfferDefinition<C, GD>; activationId: string; variant: number; startDate: number }[] = [];

    Object.keys(this.history).forEach((offerId) => {
      const offer = this.config.offers.find(({ id }) => id === offerId) ?? null;
      const currentActivation = this.getCurrentActivationData(offerId).current;

      if (!offer || !currentActivation) return;
      result.push({
        offer,
        activationId: currentActivation.activationId,
        variant: currentActivation.variant,
        startDate: currentActivation.startDate,
      });
    });

    return result;
  }
}
